// Street address abbreviation mappings based on USPS standards
const STREET_ABBREVIATIONS: Record<string, string[]> = {
  // Street Types
  avenue: ['avenue', 'ave', 'av'],
  boulevard: ['boulevard', 'blvd', 'blv'],
  circle: ['circle', 'cir', 'circ'],
  court: ['court', 'ct'],
  drive: ['drive', 'dr', 'drv'],
  expressway: ['expressway', 'expy', 'exp', 'express'],
  freeway: ['freeway', 'fwy', 'frwy'],
  highway: ['highway', 'hwy', 'hiway'],
  lane: ['lane', 'ln'],
  parkway: ['parkway', 'pkwy', 'pky', 'pkway'],
  place: ['place', 'pl'],
  road: ['road', 'rd'],
  route: ['route', 'rte', 'rt'],
  street: ['street', 'st', 'str'],
  terrace: ['terrace', 'ter', 'terr'],
  trail: ['trail', 'trl'],
  way: ['way', 'wy'],

  // Directionals
  north: ['north', 'n', 'n.'],
  south: ['south', 's', 's.'],
  east: ['east', 'e', 'e.'],
  west: ['west', 'w', 'w.'],
  northeast: ['northeast', 'ne', 'n.e.'],
  northwest: ['northwest', 'nw', 'n.w.'],
  southeast: ['southeast', 'se', 's.e.'],
  southwest: ['southwest', 'sw', 's.w.'],
  us: ['us', 'u.s.'],

  // Common Words
  saint: ['saint', 'st'],
  mount: ['mount', 'mt'],
  fort: ['fort', 'ft'],
}

// Create reverse mapping for efficient lookup
const ABBREVIATION_LOOKUP: Record<string, string[]> = {}

Object.entries(STREET_ABBREVIATIONS).forEach(([key, variations]) => {
  variations.forEach((variation) => {
    ABBREVIATION_LOOKUP[variation.toLowerCase()] = variations
  })
})

/**
 * Generates all possible variations of an address by expanding/contracting abbreviations
 */
export function generateAddressVariations(address: string): string[] {
  if (!address || address.trim().length === 0) {
    return []
  }

  const variations = new Set<string>()
  const normalizedAddress = address.trim()

  // Always include the original address
  variations.add(normalizedAddress)

  // Split address into words for processing
  const words = normalizedAddress.toLowerCase().split(/\s+/)

  // Generate variations by replacing each word with its abbreviation alternatives
  function generateVariationsRecursive(
    wordIndex: number,
    currentVariation: string[]
  ): void {
    if (wordIndex >= words.length) {
      // Reconstruct the address and add to variations
      const variation = currentVariation.join(' ')
      variations.add(variation)
      return
    }

    const currentWord = words[wordIndex]
    const alternatives = ABBREVIATION_LOOKUP[currentWord] || [currentWord]

    // For each alternative of the current word, continue recursively
    alternatives.forEach((alternative) => {
      generateVariationsRecursive(wordIndex + 1, [
        ...currentVariation,
        alternative,
      ])
    })
  }

  // Generate all combinations
  generateVariationsRecursive(0, [])

  // Convert to array and apply proper casing
  return Array.from(variations).map((variation) => {
    // Apply title case to make variations more readable
    return variation
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
  })
}

/**
 * Escapes special regex characters in a string
 */
export function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}
